import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomContactUsForm from "@tCooneyBrothers/Components/CustomContactUsForm";
import Prop65Component from "@tCooneyBrothers/Components/Prop65Component";
import RandomLengthComponent from "@tCooneyBrothers/Components/RandomLengthComponent";
import { ProductAvailabilityStyles } from "@tCooneyBrothers/Overrides/Components/ProductAvailability";
import ProductListActions from "@tCooneyBrothers/Overrides/Widgets/ProductList/ProductListActions";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showPrice?: boolean;
    showAddToList?: boolean;
    showImage?: boolean;
    showCompare?: boolean;
    showTextBadges?: boolean;
    showImageBadges?: boolean;
    onClick?: React.EventHandler<React.MouseEvent>;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    description?: InjectableCss;
    infoContainer?: InjectableCss;
    fullWidth?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandItem?: InjectableCss;
    descriptionItem?: InjectableCss;
    partNumbersItem?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityAttrItem?: InjectableCss;
    availabilityItem?: InjectableCss;
    attributePropItem?: InjectableCss;
    availabilityStyles?: ProductAvailabilityStyles;
    prop65?: InjectableCss;
    attributesItem?: InjectableCss;
    attributes?: ProductAttributesStyles;
    actionsItem?: InjectableCss;
    viewReplacementHalfGridItem?: GridItemProps;
    gridContainer?: GridContainerProps;
    viewReplacementContainer?: GridContainerProps;
    viewReplacementItem?: GridItemProps;
    viewReplacementBtn?: ButtonPresentationProps;
    discontinuedText?: TypographyPresentationProps;
    replacementProductLink?: LinkPresentationProps;
    discontiunedBadgeGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    rfqItem?: GridItemProps;
    rfqWrapper?: GridContainerProps;
    rfqTextItem?: GridItemProps;
    rfqButtonItem?: GridItemProps;
    rfqButton?: InjectableCss;
    rfqText?: TypographyPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        css: css`
            background-color: #fafafa;
            display: flex;
            flex-direction: column;
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            background-color: #fff;
            padding: 0 15px;
            [class^="StyledButton"] {
                margin: 0 50px 15px;
            }
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandItem: {
        css: css`
            padding: 5px 15px 5px 15px;
        `,
    },
    descriptionItem: {
        css: css`
            padding: 15px;
        `,
    },
    partNumbersItem: {
        css: css`
            padding: 5px 0 0;
        `,
    },
    availabilityAttrItem: {
        css: css`
            display: flex;
            align-items: center;
            height: 68px;
            padding: 10px 10px;
            [class^="GridOffset"] {
                margin-top: 0;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex !important;
                            height: auto !important;
                        `,
                        css`
                            display: block;
                        `,
                        css`
                            display: block;
                            height: 70px;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    prop65: {
        css: css`
            margin-left: auto;
            align-items: center;
        `,
    },
    attributePropItem: {
        css: css`
            padding: 15px 0 15px 5px;
            align-items: center;
            margin-left: auto;
            span,
            button {
                font-size: 11px;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 5px 0 5px 5px;
                        `,
                        css`
                            padding: 5px 0 5px 5px;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    availabilityItem: {
        // width: [12, 12, 12, 12, 7],
        css: css`
            align-items: center;
            padding-right: 0;
            [data-test-selector="callForAvailability"] {
                font-size: 11px;
            }
        `,
    },
    attributesItem: {
        // width: [12, 12, 12, 7, 7],
        css: css`
            padding: 15px 15px 10px;
            flex-direction: column;
            > div:first-child {
                > div[class^="sc"] {
                    margin-top: -5px;
                }
            }
        `,
    },

    actionsItem: {
        // width: [12, 12, 12, 5, 5],
        css: css`
            align-items: flex-end;
            [class^="StyledButton"] {
                text-transform: uppercase;
            }
        `,
    },

    infoContainer: {
        css: css`
            display: flex;
            align-items: flex-end;
            height: auto;
            margin-top: auto;
        `,
    },
    fullWidth: {
        css: css`
            width: 100%;
        `,
    },
    viewReplacementBtn: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            min-height: 40px;
            line-height: 1;
            backface-visibility: hidden;
            word-break: keep-all;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414;
            width: 100%;
            margin: 0 auto;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
        `,
    },
    viewReplacementHalfGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding-bottom: 0;
            [class^="GridOffset"] {
                margin-bottom: 0;
            }
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: flex-end;
        `,
    },
    discontinuedText: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            padding-bottom: 10px;
            color: #3674ca;
        `,
    },
    replacementProductLink: {
        css: css`
            width: 60%;
            margin: 0 auto;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    viewReplacementItem: {
        width: 12,
        css: css`
            padding: 15px;
        `,
    },
    discontiunedBadgeGridItem: {
        width: 12,
        css: css`
            padding: 5px 15px 5px 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        justify-content: center;
                    `,
                    css`
                        display: flex;
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    rfqItem: {
        width: 5,
    },
    rfqWrapper: {
        offsetCss: css`
            flex-direction: column;
        `,
    },
    rfqTextItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
            padding-left: 28px;
        `,
    },
    rfqText: {
        weight: "bold",
        size: 15,
        css: css`
            color: #2450a8;
        `,
    },
    rfqButtonItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
            padding-top: 0;
        `,
    },
    rfqButton: {
        css: css`
            cursor: pointer;
            width: 100%;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414;
            padding: 0 25px;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
        `,
    },
    successModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: 3px solid #ef3742;
                background: #fafafa;
            `,
            modalContent: css`
                padding: 30px;
                padding-top: 0;
            `,
            modalBody: css`
                max-height: calc(100% - 40px) !important;
            `,
        },
    },
    messageTextGridItem: {
        width: 12,
        css: css`
            padding-top: 25px;
            padding-bottom: 0;
        `,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation: FC<Props> = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showPrice,
    showAddToList,
    showImage,
    showCompare,
    showTextBadges,
    showImageBadges,

    onClick,
}) => {
    const { product } = productContext;
    const showDiscontinuedTag = product.properties?.discontinuedProduct?.toLowerCase() === "true" || false;
    if (!product) {
        return null;
    }
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const donotDisplayPrice = product.properties?.donotDisplayPrice?.toLowerCase() === "true" || false;
    const replacementProductPath = product.properties?.replacementProductURL;
    const onButtonClickHandler = () => {
        setIsModalOpen(true);
    };
    const continueButtonClickHandlerToParent = (modalState: boolean) => {
        modalState = false;
        setIsModalOpen(modalState);
        clearForm();
    };
    const contactUsModalCloseHandler = () => setIsModalOpen(false);
    const availability = productContext.productInfo?.inventory?.inventoryAvailabilityDtos?.[0].availability;
    const quantity = productContext.productInfo?.qtyOrdered;
    const productTitle = product.productTitle;
    const productNumber = product.productNumber;
    const brandName = product.brand?.name;
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.description}>
                {showImage && showCompare && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <ProductListProductImage
                            showImage={showImage}
                            showCompare={showCompare}
                            showTextBadge={showTextBadges}
                            showImageBadge={showImageBadges}
                        />
                    </StyledWrapper>
                )}
                {product.brand && showBrand && (
                    <StyledWrapper {...styles.brandItem}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <StyledWrapper {...styles.descriptionItem}>
                        <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                    </StyledWrapper>
                )}
            </StyledWrapper>

            {showDiscontinuedTag ? (
                <GridItem {...styles.viewReplacementHalfGridItem}>
                    <GridContainer {...styles.gridContainer} gap={0}>
                        <GridItem {...styles.viewReplacementItem}>
                            <Typography {...styles.discontinuedText} as="h2">
                                {translate("This item has been discontinued by the manufacturer")}{" "}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.discontiunedBadgeGridItem}>
                            <LazyImage src="/UserFiles/Images/discontinued-sm.png" />
                            <StyledWrapper {...styles.attributePropItem}>
                                {product.properties.prop65 && (
                                    <Prop65Component
                                        messageToDisplay={product.properties.prop65}
                                        {...styles.prop65}
                                    ></Prop65Component>
                                )}
                            </StyledWrapper>
                        </GridItem>
                        {product.detail?.replacementProductId && (
                            <GridItem {...styles.viewReplacementItem}>
                                <Link
                                    {...styles.replacementProductLink}
                                    href={replacementProductPath}
                                    onClick={onClick}
                                    data-test-selector="replacementProductLink"
                                >
                                    <Button {...styles.viewReplacementBtn} id={`View-Replacement-${product.id}`}>
                                        {translate("View Replacment")}
                                    </Button>
                                </Link>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            ) : (
                <StyledWrapper {...styles.infoContainer}>
                    <StyledWrapper {...styles.fullWidth}>
                        {showPartNumbers && (
                            <StyledWrapper {...styles.partNumbersItem}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.partNumbersStyles}
                                />
                            </StyledWrapper>
                        )}

                        {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                            <StyledWrapper {...styles.availabilityAttrItem} data-test-selector="availabilityAttribute">
                                {showAvailability && (
                                    <StyledWrapper {...styles.availabilityItem}>
                                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.attributePropItem}>
                                    {product.properties.prop65 && (
                                        <Prop65Component
                                            messageToDisplay={product.properties.prop65}
                                            {...styles.prop65}
                                        ></Prop65Component>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                        {showPrice && showAddToList && !donotDisplayPrice && (
                            <StyledWrapper {...styles.actionsItem} data-test-selector="actionGrid">
                                <ProductListActions showPrice={showPrice} showAddToList={showAddToList} />
                            </StyledWrapper>
                        )}

                        {donotDisplayPrice && (
                            <GridContainer {...styles.rfqWrapper}>
                                <GridItem {...styles.rfqTextItem}>
                                    <Typography {...styles.rfqText}>
                                        {translate("Pricing Available Upon Request")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.rfqButtonItem}>
                                    <Button {...styles.rfqButton} onClick={onButtonClickHandler}>
                                        {translate("Request Quote")}
                                    </Button>
                                    <Modal
                                        headline={translate("Request Quote")}
                                        contentLabel="Request Quote"
                                        {...styles.successModal}
                                        isOpen={isModalOpen}
                                        handleClose={contactUsModalCloseHandler}
                                    >
                                        <GridContainer {...styles.modalContainer}>
                                            <GridItem {...styles.messageTextGridItem}>
                                                <CustomContactUsForm
                                                    availability={availability}
                                                    parentModalState={continueButtonClickHandlerToParent}
                                                    productQty={quantity}
                                                    productTitle={productTitle}
                                                    productNumber={productNumber}
                                                    brandName={brandName}
                                                    isRfq={true}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </Modal>
                                </GridItem>
                            </GridContainer>
                        )}

                        {product.properties?.randomLengthItem?.toLowerCase() === "true" && (
                            <StyledWrapper {...styles.attributesItem}>
                                <RandomLengthComponent displayRandomLength={true}></RandomLengthComponent>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
